import { Box, Tooltip } from '@mui/material'
import React from 'react'
import { BeeCrewShadows } from '../../styles/theme-configuration'

type ProfileActionTileProps = {
  Icon: React.ElementType
  title: string
  handleClick: (event: React.SyntheticEvent) => void
  disabled?: boolean
}

type Size = 'small' | 'medium' | 'large'

const sizes: Record<Size, string> = {
  small: '30px',
  medium: '60px',
  large: '72px',
}

const iconConteinerSizes: Record<Size, string> = {
  small: '24px',
  medium: '34px',
  large: '42px',
}

type TileIconProps = {
  size?: Size
}

const TileIcon = ({ size = 'small', children }: React.PropsWithChildren<TileIconProps>) => {
  return (
    <Box
      sx={{
        width: iconConteinerSizes[size],
        height: iconConteinerSizes[size],
        bgcolor: 'background.main',
        borderRadius: 0.75,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {children}
    </Box>
  )
}

const baseStyles = {
  borderRadius: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  transition: 'all 300ms ease-in-out',
  '&:focus-visible': {
    svg: {
      transition: 'all 300ms ease-in-out',
    },
  },
  '&:hover': {
    svg: {
      transition: 'all 300ms ease-in-out',
    },
  },
}

const colors = {
  active: {
    cursor: 'pointer',
    bgcolor: 'primary.main',
    '&:focus-visible': {
      backgroundColor: 'primary.main',
      boxShadow: BeeCrewShadows.primary.hover,
      svg: {
        transition: 'all 300ms ease-in-out',
        color: 'primary.main',
      },
    },
    '&:hover': {
      backgroundColor: 'primary.mainHover',
      boxShadow: BeeCrewShadows.primary.hover,
      svg: {
        transition: 'all 300ms ease-in-out',
        color: 'primary.mainHover',
      },
    },
    boxShadow: BeeCrewShadows.primary.base,
  },
  disabled: {
    bgcolor: 'background.elevation9',
    svg: {
      color: 'background.elevation9',
    },
    boxShadow: BeeCrewShadows.secondary.base,
  },
}

const padding: Record<Size, string> = {
  small: '8px',
  medium: '16px',
  large: '24px',
}

const iconSize: Record<Size, string> = {
  small: '14px',
  medium: '16px',
  large: '24px',
}

const ProfileActionTile = ({ Icon, title, handleClick, disabled = false }: ProfileActionTileProps) => {
  const handleEnterDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !disabled) {
      handleClick(event)
    }
  }

  let size: Size = 'medium'

  return (
    <Tooltip title={title}>
      <Box
        sx={{
          ...baseStyles,
          ...colors[disabled ? 'disabled' : 'active'],
          gap: padding[size],
          height: sizes[size],
          width: sizes[size],
        }}
        onClick={event => {
          if (!disabled) {
            handleClick(event)
          }
        }}
        onKeyDown={handleEnterDown}
      >
        <TileIcon size={size}>
          <Icon sx={{ color: 'primary.main', width: iconSize[size], height: iconSize[size] }} />
        </TileIcon>
      </Box>
    </Tooltip>
  )
}

export default ProfileActionTile
